/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { PanelMenu } from 'primereact/panelmenu';
import styled from 'styled-components';
import { MenuItem } from 'primereact/menuitem';
import React from 'react';
import { RecapLink } from '@/pages/DocsPage/DocsLayout/PanelMenuDoc/RecapLink';

export interface Heading {
  level: string;
  title: string;
  ref: string;
}

export interface TreeHeading extends Heading {
  children: TreeHeading[];
}

const CPanelMenu = styled(PanelMenu)`
  font-size: 12px;
  font-weight: 500;
  ul {
    list-style-type: none;
    padding-left: 1.5rem;
    li {
      margin: 6px 0;
    }
  }
  svg {
    margin-right: 6px;
  }
  a {
    all: unset;
    cursor: pointer;
    color: var(--text-color);
    &:hover {
      color: var(--primary-color);
    }
  }
`;

interface Props {
  className?: string;
  docWrapperId: string;
  recapTreeLinks: Heading[];
  activeLink?: string;
}

const organizeHeadingsHierarchy = (headings: Heading[]): TreeHeading[] => {
  const headingsToProcess: Heading[] = [...headings];

  // Recursive function to build the hierarchy
  const buildHierarchy = (currentLevel: number): TreeHeading[] => {
    const result: TreeHeading[] = [];

    while (headingsToProcess.length > 0) {
      const currentHeading = headingsToProcess[0];
      const headingLevel = parseInt(currentHeading.level.substring(1));

      if (headingLevel < currentLevel) {
        break;
      }

      // If this is a heading at our current level, add it to results
      if (headingLevel === currentLevel) {
        // Remove from array since we're processing it
        const heading = headingsToProcess.shift() as Heading;

        // Create a node with children
        const node: TreeHeading = {
          ...heading,
          children: buildHierarchy(currentLevel + 1)
        };

        result.push(node);
      }
      // If this is a deeper level than we're currently processing, we've skipped a level
      else if (headingLevel > currentLevel) {
        // Build a hierarchy starting from this new depth
        const children = buildHierarchy(headingLevel);

        // If we have children from the deeper level, add them to the last node
        if (result.length > 0 && children.length > 0) {
          if (!result[result.length - 1].children) {
            result[result.length - 1].children = [];
          }
          result[result.length - 1].children.push(...children);
        } else if (children.length > 0) {
          result.push(...children);
        }
      }
    }

    return result;
  };

  // Start building from h1 (level 1)
  return buildHierarchy(1);
};

const transformToPrimeReactMenu = (headingTree: TreeHeading[], docWrapperId: string, active?: string) => {
  const processNode = (node: TreeHeading) => {
    const menuItem: MenuItem = {
      label: node.title,
      url: node.ref,
      expanded: true,
      template: () => {
        const isActive = active === node.ref;
        return <RecapLink isActive={isActive} docWrapperId={docWrapperId} node={node} />;
      }
    };

    if (node.children && node.children.length > 0) {
      menuItem.items = node.children.map((child) => processNode(child));
    }

    return menuItem;
  };

  return headingTree.map((node) => processNode(node));
};

export const PanelMenuDoc: React.FC<Props> = ({ className, recapTreeLinks, docWrapperId, activeLink }) => {
  const headingNodes = organizeHeadingsHierarchy(recapTreeLinks);
  const menuItems = transformToPrimeReactMenu(headingNodes, docWrapperId, activeLink);
  return <CPanelMenu unstyled className={className} multiple model={menuItems} />;
};
